import {
  Badge as ChakraBadge,
  BadgeProps,
  Circle,
  SquareProps,
  Text,
  TextProps,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

export const Badge: React.FC<BadgeProps> = ({ children, ...restProps }) => {
  return (
    <ChakraBadge
      as="div"
      display="flex"
      variant="subtle"
      height="30px"
      alignItems="center"
      justifyContent="center"
      p="0 10px"
      {...restProps}
    >
      {children}
    </ChakraBadge>
  );
};

export const CircleBadge: React.FC<
  SquareProps & {
    children?: React.ReactNode;
    withTooltip?: boolean;
    tooltipLabel?: string;
    showTooltip?: boolean;
  }
> = ({
  children,
  withTooltip = false,
  tooltipLabel,
  showTooltip,
  ...restProps
}) => {
  const displayTooltip = showTooltip || Boolean(withTooltip && tooltipLabel);
  return displayTooltip ? (
    <Tooltip hasArrow label={tooltipLabel}>
      <Circle {...restProps} size={restProps.size || '32px'}>
        {children}
      </Circle>
    </Tooltip>
  ) : (
    <Circle {...restProps} size={restProps.size || '32px'}>
      {children}
    </Circle>
  );
};

/**
 * Returns a cirlce badge with optional single initial character and tooltip
 * @param text Will take the first character of the text and display it in a circle badge
 * @param withTooltip If true, will display a tooltip with the full text
 * @param tooltipLabel If provided, will display this text in the tooltip instead of the text prop
 */
export const InitialBadge: React.FC<
  SquareProps & {
    text?: string;
    textColor?: TextProps['color'];
    withTooltip?: boolean;
    tooltipLabel?: string;
  }
> = ({ text, withTooltip = false, tooltipLabel, textColor, ...restProps }) => {
  const color = useColorModeValue('white', 'black');

  const showTooltip = Boolean(
    (withTooltip && text) || (withTooltip && tooltipLabel),
  );
  return (
    <CircleBadge
      {...restProps}
      withTooltip={withTooltip}
      tooltipLabel={tooltipLabel || text}
      showTooltip={showTooltip}
    >
      {text && (
        <Text fontSize="sm" color={textColor || color}>
          {text.charAt(0)}
        </Text>
      )}
    </CircleBadge>
  );
};

export const LogoBadge: React.FC<
  SquareProps & {
    logo: React.ReactElement;
    withTooltip?: boolean;
    tooltipLabel?: string;
  }
> = ({ logo, withTooltip = false, tooltipLabel, ...restProps }) => {
  const showTooltip = Boolean(withTooltip && tooltipLabel);
  return (
    <CircleBadge
      {...restProps}
      withTooltip={withTooltip}
      tooltipLabel={tooltipLabel}
      showTooltip={showTooltip}
    >
      {logo}
    </CircleBadge>
  );
};
